"use client";

import { Megaphone } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

/**
 * Seller "store notice" banner shell.
 *
 * There used to be a `darkUi` boolean picking a second, hand-tuned amber ramp
 * for the forced-dark overlays (cart sheet, chat listing sheet). It was never
 * needed: `dark:` variants fire on descendants of the panel's `dark` class, so
 * the theme-aware ramp below already resolves to the dark values inside an
 * overlay — which is exactly how the sibling call site
 * `components/cart/checkout-form.tsx` has always rendered this banner. The two
 * ramps disagreed only on the container (border/bg tint); every text colour was
 * byte-identical. Collapsed onto the theme-aware one so the banner keeps working
 * when the panel turns light (step 2f).
 *
 * @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §4 (step 2c)
 */
export function storeNoticeEmberShellClass(className?: string) {
  return cn(
    "flex w-full items-start gap-2.5 rounded-xl border p-3 text-base",
    "border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/25 dark:text-amber-50",
    className,
  );
}

export function SellerCheckoutNoteBanner({
  note,
  className,
}: {
  note: string;
  className?: string;
}) {
  const t = useTranslations("profile.info");
  const text = note.trim();
  if (!text) return null;

  return (
    <div className={storeNoticeEmberShellClass(className)}>
      <Megaphone
        className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400"
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm mb-1 text-amber-900 dark:text-amber-200">
          {t("storeNotice")}
        </p>
        <p
          className={cn(
            // overflow-wrap:anywhere (not break-words) so a long unbreakable URL
            // collapses its min-content width — otherwise it forces the parent grid
            // wider than the sheet and overflow-x-hidden clips the right edge
            // (Order Summary / Payment values). break-words wouldn't fix the sizing.
            "whitespace-pre-wrap leading-snug [overflow-wrap:anywhere]",
            "text-amber-900/90 dark:text-amber-100/90",
          )}
        >
          {text}
        </p>
      </div>
    </div>
  );
}
